import {
  Box,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { FormikProps } from 'formik';
import React, { useMemo, useState } from 'react';

import { ReportingPropertyConfig } from './ReportingConfig';
import { FormFieldLabel } from 'packages/common-frontend/components/FormField';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import { MultiSelect } from 'packages/common-frontend/components/MultiSelect';
import * as utils from 'packages/common-frontend/utils';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

const ReportTypeSelectOptions = CreditAgreement.DomainEntity.ReportConfigTypeIterable.map(
  (type) => ({ label: type, value: type }),
);

const ReportContainer: React.FC<{
  reportingProperty: CreditAgreement.DomainEntity.ReportingProperty;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fullPropertyKey: string;
  columnWidth: string;
  headersLength: number;
  propertyIdx: number;
  basePropertyKey: string;
}> = ({
  reportingProperty,
  wipConfig,
  fullPropertyKey,
  columnWidth,
  headersLength,
  propertyIdx,
  basePropertyKey,
}) => {
  const {
    name,
    reportConfigTypes,
    description,
    _note_,
    _isWrittenIn_,
  } = reportingProperty;

  const [isOpen, setIsOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<
    CreditAgreement.DomainEntity.ReportConfigType[]
  >(reportConfigTypes);
  const [titleText, setTitleText] = useState(reportConfigTypes.join(', '));

  const handleChange = (
    newValue: CreditAgreement.DomainEntity.ReportConfigType,
  ) => {
    const index = selectedOptions.indexOf(newValue);

    if (index >= 0) {
      // only remove option if multiple are selected
      if (selectedOptions.length === 1) {
        return selectedOptions;
      }

      selectedOptions.splice(index, 1);
      setSelectedOptions(selectedOptions);
    } else {
      selectedOptions.push(newValue);
    }

    setTitleText(selectedOptions.join(', '));

    return selectedOptions;
  };

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700">
        <Td w={columnWidth}>{name}</Td>
        <Td>{reportConfigTypes.join(', ')}</Td>
        <Td w={columnWidth}>{utils.displayValueOrEmpty(description)}</Td>

        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <Td w={columnWidth}>{utils.displayValueOrEmpty(_note_)}</Td>
            <Td w={columnWidth}>{getBooleanSymbol(_isWrittenIn_)}</Td>
            <Td w={columnWidth}>
              <EditIconButton
                ariaLabel="edit"
                label="Edit"
                onClick={(event) => {
                  setIsOpen(true);
                  if (event) {
                    event.stopPropagation();
                  }
                }}
              />
              <DeleteArrayPropertyButton
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                idx={propertyIdx}
              />
            </Td>
          </>
        )}

        <Td w="fit-content">
          {isConfigOpen ? (
            <IconButton
              aria-label="close config"
              icon={<FiChevronUp />}
              onClick={() => setIsConfigOpen(false)}
              variant="ghost"
            />
          ) : (
            <IconButton
              aria-label="display config"
              icon={<FiChevronDown />}
              onClick={() => setIsConfigOpen(true)}
              variant="ghost"
            />
          )}
        </Td>
      </Tr>

      {isConfigOpen && (
        <ReportingPropertyConfig
          reportingProperty={reportingProperty}
          wipConfig={wipConfig}
          configPropertyKey={`${fullPropertyKey}._config_`}
          columnLength={headersLength + 1} // Add + 1 to include "config" icon
        />
      )}

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <EditPropertyModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          wipConfig={wipConfig}
          basePropertyKey={fullPropertyKey}
          modalHeaderLabel="Reporting Mechanics"
        >
          {(
            localFormProps: FormikProps<CreditAgreement.DomainEntity.ReportingProperty>,
          ) => (
            <Box w="100%">
              <Flex w="95%" alignItems="center">
                <FormFieldLabel label="Report Type" labelWidth="44" />
                <MultiSelect<CreditAgreement.DomainEntity.ReportConfigType>
                  onChange={(val) => {
                    const selectedTypes = handleChange(val);
                    localFormProps.setFieldValue(
                      'reportConfigTypes',
                      selectedTypes,
                    );
                  }}
                  onMenuClosed={() => {}}
                  options={ReportTypeSelectOptions}
                  selectedOptions={selectedOptions}
                  titleText={titleText}
                />
              </Flex>
            </Box>
          )}
        </EditPropertyModal>
      )}
    </>
  );
};

const REPORTING_BASE_TABLE_HEADERS = ['Name', 'Report Type', 'Description'];
const REPORTING_WIP_TABLE_HEADERS = ['Note', 'Is Written in', 'Action'];

export const ReportingTable: React.FC<{
  reportingMechanics: CreditAgreement.DomainEntity.ReportingMechanics;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
}> = ({ reportingMechanics, wipConfig, basePropertyKey }) => {
  const { tableHeaders, columnWidth } = useMemo<{
    tableHeaders: string[];
    columnWidth: string;
  }>(() => {
    const headers: string[] = wipConfig
      ? [...REPORTING_BASE_TABLE_HEADERS, ...REPORTING_WIP_TABLE_HEADERS]
      : REPORTING_BASE_TABLE_HEADERS;
    const width: string = `${Math.floor(95 / (headers.length + 1))}%`;
    return { tableHeaders: headers, columnWidth: width };
  }, [wipConfig]);

  if (!reportingMechanics.length) {
    return <Text>There are currently no sections set up for Reporting.</Text>;
  }

  return (
    <Box id="reporting-table-container">
      {reportingMechanics.map((property, idx) => {
        const fullPropertyKey = `${basePropertyKey}[${[idx]}]`;
        return (
          <Box key={fullPropertyKey} mb="5">
            <Table
              style={{ tableLayout: 'fixed' }}
              borderWidth="1px"
              id={`reporting-table-${idx}`}
            >
              <Thead>
                <Tr>
                  {tableHeaders.map((header) => (
                    <Th width={columnWidth} key={header}>
                      {header}
                    </Th>
                  ))}
                  <Th width="5%" />
                </Tr>
              </Thead>

              <Tbody>
                <ReportContainer
                  reportingProperty={property}
                  wipConfig={wipConfig}
                  fullPropertyKey={fullPropertyKey}
                  columnWidth={columnWidth}
                  headersLength={tableHeaders.length}
                  propertyIdx={idx}
                  basePropertyKey={basePropertyKey}
                />
              </Tbody>
            </Table>
          </Box>
        );
      })}
    </Box>
  );
};
